import { IInventoryBranchDetails, IInventoryVendorGSTCertificateDetails } from "@/api_framework/api_modals/Inventory";
import SoftButton from "@/app/components/ui/Buttons/SoftButton";
import Input from "@/app/components/ui/Inputs/Input";
import InputWithFileUpload from "@/app/components/ui/Inputs/InputWithFileUpload";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { useConfigurationCityDetailByPincodeAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityDetailByPincodeAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useUtils } from "@/app/hooks/useUtils";
import { PlusCircleIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { has } from "lodash";
import React, { useEffect } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { IAddVendorsBrancDetailsFormFields } from "..";

const BranchContactDetails: React.FC<{
  idx: number;
  isForEdit: boolean;
  formHook: UseFormReturn<IAddVendorsBrancDetailsFormFields, any>;
}> = ({ formHook, idx, isForEdit }) => {
  const { forEmail, forMobile, forAlphaNumericWithGST, forOnlyAlphabetAndSpaces } = useFormValidations();

  const { base64ToValidURL, fileListToBase64 } = useUtils();

  const updateMediaInBranches = async () => {
    const bsae64Files = await fileListToBase64(
      formHook.getValues(`branches.${idx}.gst_certificate_attachment_file`),
    ).then((res) => {
      return res;
    });
    formHook.setValue(`branches.${idx}.gst_certificate`, bsae64Files[0] as IInventoryVendorGSTCertificateDetails);
  };
  useEffect(() => {
    updateMediaInBranches();
  }, [formHook.watch(`branches.${idx}.gst_certificate_attachment_file`)]);
  const validateAlternateMobile = () => {
    formHook.trigger(`branches.${idx}.contact_details.primary_phone`).then((isValid) => {
      if (isValid) {
        if (
          formHook.getValues(`branches.${idx}.contact_details.primary_phone`) ===
          formHook.getValues(`branches.${idx}.contact_details.alternate_phone`)
        ) {
          formHook.setError(`branches.${idx}.contact_details.alternate_phone`, {
            type: "validate",
            message: "",
          });
        }
      }
    });
  };

  return (
    <div className="mt-8">
      <p className="font-semibold text-md">Contact Person</p>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Name</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          errorMessages={[{ message: "Vendor name is required", type: "required" }, forOnlyAlphabetAndSpaces.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.contact_details?.name
                ? formHook?.formState?.errors?.branches[idx]?.contact_details?.name
                : null
              : null
          }
          placeholder="Contact Person Name"
          register={formHook.register(`branches.${idx}.contact_details.name` as const, {
            required: true,
            ...forOnlyAlphabetAndSpaces.validations,
            onChange: () => formHook.trigger(`branches.${idx}.contact_details.name`),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.contact_details.name`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.contact_details.name`);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Email</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          errorMessages={[{ message: "Email is required", type: "required" }, forEmail.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.contact_details?.email
                ? formHook?.formState?.errors?.branches[idx]?.contact_details?.email
                : null
              : null
          }
          placeholder="Contact Person Email"
          register={formHook.register(`branches.${idx}.contact_details.email` as const, {
            required: true,
            ...forEmail.validations,
            onChange: () => formHook.trigger(`branches.${idx}.contact_details.email`),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.contact_details.email`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.contact_details.email`);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Contact Phone</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          errorMessages={[{ message: "Mobile is required", type: "required" }, forMobile.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.contact_details?.primary_phone
                ? formHook?.formState?.errors?.branches[idx]?.contact_details?.primary_phone
                : null
              : null
          }
          maxLength={10}
          placeholder="Contact Person Phone"
          register={formHook.register(`branches.${idx}.contact_details.primary_phone` as const, {
            required: true,
            onChange: () => formHook.trigger(`branches.${idx}.contact_details.primary_phone`),
            ...forMobile.validations,
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.contact_details.primary_phone`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.contact_details.primary_phone`);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Alternate Phone</span>
        </div>
        <Input
          errorMessages={[
            forMobile.errors,
            { message: "Max 10", type: "maxLength" },
            {
              type: "validate",
              message: "Should not be same as primary.",
            },
          ]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.contact_details?.alternate_phone
                ? formHook?.formState?.errors?.branches[idx]?.contact_details?.alternate_phone
                : null
              : null
          }
          maxLength={10}
          placeholder="Contact Person Phone"
          register={formHook.register(`branches.${idx}.contact_details.alternate_phone` as const, {
            ...forMobile.validations,
            onChange: () => {
              validateAlternateMobile();
            },
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.contact_details.alternate_phone`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.contact_details.alternate_phone`);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">GSTN Number</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          maxLength={15}
          errorMessages={[{ type: "required", message: "GSTN is required" }, forAlphaNumericWithGST.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.gst_number
                ? formHook?.formState?.errors?.branches[idx]?.gst_number
                : null
              : null
          }
          placeholder="GSTN Number"
          register={formHook.register(`branches.${idx}.gst_number` as const, {
            required: true,
            ...forAlphaNumericWithGST.validations,
            onChange: () => formHook.trigger(`branches.${idx}.gst_number`),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.gst_number`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.gst_number`);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">GSTN Certificate</span>
        </div>
        <div className="w-full sm:w-2/6 lg:w-1/4">
          <InputWithFileUpload
            variant="DRAG_AND_DROP"
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.gst_certificate_attachment_file
                  ? formHook?.formState?.errors?.branches[idx]?.gst_certificate_attachment_file
                  : null
                : null
            }
            onClear={() => {
              formHook.resetField(`branches.${idx}.gst_certificate_attachment_file`);
            }}
            files={formHook.watch(`branches.${idx}.gst_certificate_attachment_file`)}
            register={formHook?.register(`branches.${idx}.gst_certificate_attachment_file`, {
              required: false,
              onChange: () => formHook.trigger(`branches.${idx}.gst_certificate_attachment_file`),
              validate: {
                maxSize: (files: FileList) => {
                  if (!files) return true; // If no files, validation passes (can change logic if required)
                  return (
                    Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                    "Each file must be smaller than 5MB"
                  );
                },
                fileType: (files: FileList) => {
                  if (!files) return true; // If no files, validation passes (can change logic if required)
                  const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                  return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                },
              },
            })}
            type="file"
            id={`branches.${idx}.gst_certificate_attachment_file`}
            errorMessages={[{ type: "required", message: "Media Required" }]}
            dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
          />

          <div className="flex items-center gap-2 my-3">
            {formHook?.watch(`branches.${idx}.gst_certificate`) &&
            has(formHook?.watch(`branches.${idx}.gst_certificate`), "base_64_string") ? (
              <img
                src={base64ToValidURL(
                  formHook?.watch(`branches.${idx}.gst_certificate`).base_64_string,
                  formHook?.watch(`branches.${idx}.gst_certificate`).file_extension,
                )}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            ) : (
              formHook?.watch(`branches.${idx}.gst_certificate`)?.document_url && (
                <img
                  src={formHook?.watch(`branches.${idx}.gst_certificate`)?.document_url}
                  alt=""
                  className="w-full sm:w-1/2 h-full"
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BranchBankDetailsBlock: React.FC<{
  idx: number;
  formHook: UseFormReturn<IAddVendorsBrancDetailsFormFields, any>;
}> = ({ formHook, idx }) => {
  const { forAlphaNumericWithoutSpace, forOnlyNumbers, forOnlyAlphabetAndSpaces } = useFormValidations();
  const matchAccountNumber = (index: number, confirmAccountNumber: string) => {
    const accountNumber = formHook.getValues(`branches.${index}.bank_details.account_number`);
    if (accountNumber.trim() !== confirmAccountNumber.trim()) {
      formHook.setError(`branches.${idx}.bank_details.confirmed_account_number`, {
        type: "validate",
        message: "Confirm Account Number don't match.",
      });
    } else {
      formHook.clearErrors(`branches.${idx}.bank_details.confirmed_account_number`);
    }
  };
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start mt-5">
      <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
        <span className="font-normal text-black text-xs">Bank Details</span>
      </div>
      <div className="block">
        <Input
          errorMessages={[forOnlyNumbers.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.bank_details?.account_number
                ? formHook?.formState?.errors?.branches[idx]?.bank_details.account_number
                : null
              : null
          }
          placeholder="Account Number"
          register={formHook.register(`branches.${idx}.bank_details.account_number` as const, {
            required: false,
            ...forOnlyNumbers.validations,
            onChange: () => formHook.trigger(`branches.${idx}.bank_details.account_number`),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-xs"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.bank_details.account_number`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.bank_details.account_number`);
          }}
        />
        <Input
          errorMessages={[
            { message: "Confirm Account Number is required", type: "required" },
            { type: "validate", message: "Confirm ACC and ACC did not match." },
            forOnlyNumbers.errors,
          ]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.bank_details?.confirmed_account_number
                ? formHook?.formState?.errors?.branches[idx]?.bank_details.confirmed_account_number
                : null
              : null
          }
          placeholder="Confirm Account Number"
          register={formHook.register(`branches.${idx}.bank_details.confirmed_account_number` as const, {
            required: false,
            ...forOnlyNumbers.validations,
            onChange: (e) => matchAccountNumber(idx, e.target.value),
          })}
          className="border-slate-400 mt-5 rounded-md w-full sm:w-[300px] h-9 text-xs"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.bank_details.confirmed_account_number`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.bank_details.confirmed_account_number`);
          }}
        />
        <Input
          errorMessages={[{ message: "Bank name is required", type: "required" }, forOnlyAlphabetAndSpaces.errors]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.bank_details?.bank_name
                ? formHook?.formState?.errors?.branches[idx]?.bank_details?.bank_name
                : null
              : null
          }
          placeholder="Bank Name"
          register={formHook.register(`branches.${idx}.bank_details.bank_name` as const, {
            required: false,
            ...forOnlyAlphabetAndSpaces.validations,
          })}
          className="border-slate-400 mt-5 rounded-md w-full sm:w-[300px] h-9 text-xs"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.bank_details.bank_name`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.bank_details.bank_name`);
          }}
        />
        <div className="flex justify-start items-center gap-x-2 mt-5">
          <Input
            errorMessages={[
              { message: "Beneficiary name is required", type: "required" },
              forOnlyAlphabetAndSpaces.errors,
            ]}
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.bank_details?.beneficiary_name
                  ? formHook?.formState?.errors?.branches[idx]?.bank_details.beneficiary_name
                  : null
                : null
            }
            placeholder="Beneficiary Name"
            register={formHook.register(`branches.${idx}.bank_details.beneficiary_name` as const, {
              required: false,
              ...forOnlyAlphabetAndSpaces.validations,
            })}
            className="border-slate-400 rounded-md w-full sm:w-[150px] h-9 text-xs"
            inputClassName="h-[38px] rounded-md"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formHook?.setValue(`branches.${idx}.bank_details.beneficiary_name`, e.target?.value);
              formHook.clearErrors(`branches.${idx}.bank_details.beneficiary_name`);
            }}
          />
          <Input
            errorMessages={[{ message: "IFSC is required", type: "required" }, forAlphaNumericWithoutSpace.errors]}
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.bank_details?.ifs_code
                  ? formHook?.formState?.errors?.branches[idx]?.bank_details?.ifs_code
                  : null
                : null
            }
            placeholder="IFSC e.g SBIN0001234"
            register={formHook.register(`branches.${idx}.bank_details.ifs_code` as const, {
              required: false,
              ...forAlphaNumericWithoutSpace.validations,
            })}
            className="border-slate-400 rounded-md w-full sm:w-[150px] h-9 text-xs"
            inputClassName="h-[38px] rounded-md"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formHook?.setValue(`branches.${idx}.bank_details.ifs_code`, e.target?.value);
              formHook.clearErrors(`branches.${idx}.bank_details.ifs_code`);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const BranchAddressDetailsBlock: React.FC<{
  idx: number;
  formHook: UseFormReturn<IAddVendorsBrancDetailsFormFields, any>;
}> = ({ formHook, idx }) => {
  const { forOnlyNumber, forOnlyAlphabet } = useFormValidations();
  let pincodeFetch = false;
  const { cityByPincode: pincodeData, execute: fetchPincodeDetails } = useConfigurationCityDetailByPincodeAPI();

  useEffect(() => {
    const pincode = formHook?.watch(`branches.${idx}.billing_address.pincode`);
    if (pincode?.length == 6 && !pincodeFetch) {
      fetchPincodeDetails(+pincode);
      pincodeFetch = true;
    }
  }, [formHook?.watch(`branches.${idx}.billing_address.pincode`)]);

  useEffect(() => {
    if (pincodeData) {
      formHook?.setValue(`branches.${idx}.billing_address.city`, pincodeData?.District);
      formHook?.setValue(`branches.${idx}.billing_address.state`, pincodeData?.State);
    }
  }, [pincodeData]);

  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start mt-5">
      <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
        <span className="font-normal text-black text-xs">Branch Address</span>
        <span className="font-normal text-rose-600 text-xs">*</span>
      </div>
      <div className="block">
        <Input
          errorMessages={[{ message: "Address Line 1 is required", type: "required" }]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.billing_address?.address_line_1
                ? formHook?.formState?.errors?.branches[idx]?.billing_address?.address_line_1
                : null
              : null
          }
          placeholder="Address Line 1"
          register={formHook.register(`branches.${idx}.billing_address.address_line_1` as const, {
            required: true,
            onChange: () => formHook.trigger(`branches.${idx}.billing_address.address_line_1`),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-xs"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.billing_address.address_line_1`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.billing_address.address_line_1`);
          }}
        />
        <Input
          errorMessages={[{ message: "invalid Format", type: "required" }]}
          fieldError={
            formHook?.formState?.errors?.branches
              ? formHook?.formState?.errors?.branches[idx]?.billing_address?.address_line_2
                ? formHook?.formState?.errors?.branches[idx]?.billing_address?.address_line_2
                : null
              : null
          }
          placeholder="Address Line 2"
          register={formHook.register(`branches.${idx}.billing_address.address_line_2` as const, {
            required: false,
          })}
          className="border-slate-400 mt-5 rounded-md w-full sm:w-[300px] h-9 text-xs"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formHook?.setValue(`branches.${idx}.billing_address.address_line_2`, e.target?.value);
            formHook.clearErrors(`branches.${idx}.billing_address.address_line_2`);
          }}
        />
        <div className="flex justify-start items-center gap-x-2 mt-2">
          <Input
            errorMessages={[{ message: "Pincode is required", type: "required" }, forOnlyNumber.errors]}
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.billing_address?.pincode
                  ? formHook?.formState?.errors?.branches[idx]?.billing_address.pincode
                  : null
                : null
            }
            placeholder="Pincode"
            maxLength={6}
            register={formHook.register(`branches.${idx}.billing_address.pincode` as const, {
              required: true,
              ...forOnlyNumber.validations,
              onChange: () => formHook.trigger(`branches.${idx}.billing_address.pincode`),
            })}
            className="border-slate-400 rounded-md w-full sm:w-[95px] h-9 text-xs"
            inputClassName="h-[38px] rounded-md"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formHook?.setValue(`branches.${idx}.billing_address.pincode`, e.target?.value);
              formHook.clearErrors(`branches.${idx}.billing_address.pincode`);
            }}
          />
          <Input
            errorMessages={[{ message: "City is required", type: "required" }, forOnlyAlphabet.errors]}
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.billing_address?.city
                  ? formHook?.formState?.errors?.branches[idx]?.billing_address.city
                  : null
                : null
            }
            placeholder="City"
            register={formHook.register(`branches.${idx}.billing_address.city` as const, {
              required: true,
              ...forOnlyAlphabet.validations,
              onChange: () => formHook.trigger(`branches.${idx}.billing_address.city`),
            })}
            className="border-slate-400 rounded-md w-full sm:w-[95px] h-9 text-xs"
            inputClassName="h-[38px] rounded-md"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formHook?.setValue(`branches.${idx}.billing_address.city`, e.target?.value);
              formHook.clearErrors(`branches.${idx}.billing_address.city`);
            }}
          />
          <Input
            errorMessages={[{ message: "State is required", type: "required" }, forOnlyAlphabet.errors]}
            fieldError={
              formHook?.formState?.errors?.branches
                ? formHook?.formState?.errors?.branches[idx]?.billing_address?.state
                  ? formHook?.formState?.errors?.branches[idx]?.billing_address?.state
                  : null
                : null
            }
            placeholder="State"
            register={formHook.register(`branches.${idx}.billing_address.state` as const, {
              required: true,
              ...forOnlyAlphabet.validations,
              onChange: () => formHook.trigger(`branches.${idx}.billing_address.state`),
            })}
            className="border-slate-400 rounded-md w-full sm:w-[95px] h-9 text-xs"
            inputClassName="h-[38px] rounded-md"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formHook?.setValue(`branches.${idx}.billing_address.state`, e.target?.value);
              formHook.clearErrors(`branches.${idx}.billing_address.state`);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const VendorBranchDetailsFormComponent: React.FC<{
  branchesFieldArrayHook: UseFieldArrayReturn<IAddVendorsBrancDetailsFormFields, "branches", "id">;
  isForEdit: boolean;
  branchDetailsFormHook: UseFormReturn<IAddVendorsBrancDetailsFormFields, any>;
  initialState: IInventoryBranchDetails;
}> = ({ branchesFieldArrayHook: fieldFormHook, branchDetailsFormHook: formHook, initialState, isForEdit }) => {
  const { forOnlyAlphabet } = useFormValidations();
  return (
    <div className="bg-white mt-4 py-4 rounded-md">
      <form onSubmit={formHook.handleSubmit(() => {})} className="mt-8">
        <p className="font-semibold text-md">Branch Details</p>
        {fieldFormHook?.fields?.map((field, idx) => {
          return (
            <div key={field.id} className="border-gray-100 mt-4 px-2 pt-2 pb-4 border rounded-md">
              <div className="flex justify-between items-center bg-blue-50 py-3 pr-4 pl-4 rounded-md font-bold text-xs">
                <span>Branch {idx + 1}</span>
                {idx >= 1 && (
                  <button onClick={() => fieldFormHook.remove(idx)}>
                    <TrashIcon className="w-4 h-4 text-red-600 hover:scale-105 hover:text-red-500" />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Branch Name</span>
                  <span className="font-normal text-rose-600 text-xs">*</span>
                </div>
                <Input
                  errorMessages={[{ message: "Branch name is required", type: "required" }, forOnlyAlphabet.errors]}
                  fieldError={
                    formHook?.formState?.errors?.branches ? formHook.formState.errors.branches[idx]?.branch_name : null
                  }
                  placeholder="Enter Branch Name"
                  register={formHook.register(`branches.${idx}.branch_name` as const, {
                    required: true,
                    ...forOnlyAlphabet.validations,
                    onChange: () => formHook.trigger(`branches.${idx}.branch_name`),
                  })}
                  className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
                  inputClassName="h-[38px] rounded-md"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formHook?.setValue(`branches.${idx}.branch_name`, e.target?.value);
                    formHook.clearErrors(`branches.${idx}.branch_name`);
                  }}
                />
              </div>
              {/* Branch Address */}
              <div className="mt-6">
                <BranchAddressDetailsBlock formHook={formHook} idx={idx} />
              </div>
              {/* Branch Bank Details */}
              <BranchBankDetailsBlock formHook={formHook} idx={idx} />
              {/* Contact Details */}
              <BranchContactDetails isForEdit={isForEdit} formHook={formHook} idx={idx} />
            </div>
          );
        })}
        <SoftButton
          className="flex justify-start items-center gap-x-1 mt-4"
          size="SM"
          onButtonClick={(e) => {
            e.preventDefault();
            fieldFormHook.append(initialState);
          }}
        >
          <PlusCircleIcon className="w-4 h-4" />
          Add Branch
        </SoftButton>
        <div className="flex flex-row items-center gap-4 mt-4">
          <div className="shrink-0 text-xs sm:w-[250px]">
            <MandatoryLabel label="Status" />
          </div>
          <div className="shrink-0">
            <ToggleWithIcon
              onToggleChange={(status) => {
                console.log("Toggle Changed:", status);
                formHook.setValue("status", status);
              }}
              toggleState={formHook.watch("status")}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default VendorBranchDetailsFormComponent;
